import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/ui/Button";
import { MonoText } from "@/components/ui/CopyButton";

const NAV_LINKS = [
  { to: "/products", label: "Katalog" },
  { to: "/dashboard", label: "Akun Saya", auth: true },
  { to: "/admin", label: "Admin", owner: true },
  { to: "/admin/settings", label: "Settings", owner: true },
];

export function Header() {
  const { user, loading, error, signIn, signOut } = useAuth();
  const { count } = useCart();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  /** Panel nav mobile — terpisah dari menu akun (menuOpen). */
  const [navOpen, setNavOpen] = useState(false);

  async function handleSignIn() {
    try {
      await signIn();
      navigate("/dashboard");
    } catch {
      // Pesan sudah disimpan di AuthContext.error dan dirender sebagai banner
      // di bawah. Jangan navigate() di sini — itu yang dulu membuat kegagalan
      // login tampak seperti "tidak terjadi apa-apa".
    }
  }

  const visible = NAV_LINKS.filter((l) => {
    if (l.owner && user?.role !== "owner") return false;
    if (l.auth && !user) return false;
    return true;
  });

  return (
    <header className="border-b border-border bg-surface sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 md:gap-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group shrink-0">
          <div className="w-7 h-7 bg-accent flex items-center justify-center rounded-sm">
            <span className="font-heading text-sm font-extrabold text-void">A</span>
          </div>
          {/* Wordmark disembunyikan di bawah 420px. Bersama tombol tema,
              keranjang, "Masuk", dan hamburger, teks ini mendorong header 4px
              melewati layar 375px sehingga seluruh halaman bisa digeser ke
              samping. Kotak "A" tetap menjadi penanda merek. */}
          <span className="hidden min-[420px]:inline font-heading text-base font-extrabold tracking-tight text-ink-primary">
            AG5R<span className="text-accent">.</span>STORE
          </span>
        </Link>

        {/* Nav.
            Disembunyikan di bawah md: logo + 4 link + 3 tombol tidak muat di
            375px, dan sebelumnya semuanya dipaksa satu baris sampai saling
            menindih. Di mobile isinya pindah ke panel hamburger di bawah. */}
        <nav className="hidden md:flex flex-1 items-center gap-1">
          {visible.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `font-sans text-sm font-medium px-3 py-1.5 rounded-sm transition-colors ${
                  isActive
                    ? "text-accent"
                    : "text-ink-muted hover:text-ink-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Saat nav disembunyikan, ini yang mendorong tombol ke tepi kanan. */}
        <div className="flex-1 md:hidden" />

        {/* Tema + Keranjang + Auth */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            title={theme === "dark" ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
            aria-label={theme === "dark" ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
            className="w-9 h-9 flex items-center justify-center rounded-sm border border-border
                       text-ink-muted hover:text-accent hover:border-accent transition-colors"
          >
            <i
              className={`fa-solid ${theme === "dark" ? "fa-sun" : "fa-moon"} text-sm`}
              aria-hidden
            />
          </button>

          <Link
            to="/cart"
            aria-label={`Keranjang, ${count} item`}
            className="relative w-9 h-9 flex items-center justify-center rounded-sm
                       border border-border text-ink-muted
                       hover:text-accent hover:border-accent transition-colors"
          >
            <i className="fa-solid fa-cart-shopping text-sm" aria-hidden />
            {count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1
                               flex items-center justify-center rounded-full
                               bg-accent text-on-accent font-mono text-2xs font-bold">
                {count}
              </span>
            )}
          </Link>

          {loading ? (
            <span className="w-9 h-9 flex items-center justify-center text-ink-dim">
              <i className="fa-solid fa-circle-notch fa-spin text-sm" aria-hidden />
            </span>
          ) : user ? (
            // Email + role + tombol keluar dulu memakan ruang horizontal besar.
            // Sekarang diringkas ke satu avatar; detailnya ada di dalam menu.
            <div className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Menu akun"
                onClick={() => setMenuOpen((v) => !v)}
                className="w-9 h-9 flex items-center justify-center rounded-full
                           bg-accent text-on-accent font-sans text-sm font-bold
                           hover:opacity-90 transition-opacity"
              >
                {user.email.charAt(0).toUpperCase()}
              </button>

              {menuOpen && (
                <>
                  {/* Lapisan penangkap klik — menutup menu tanpa listener global. */}
                  <button
                    type="button"
                    aria-hidden
                    tabIndex={-1}
                    onClick={() => setMenuOpen(false)}
                    className="fixed inset-0 z-40 cursor-default"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-60 z-50 rounded-lg border border-border-strong
                               bg-surface shadow-lift overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-border">
                      <p className="font-sans text-sm text-ink-primary truncate">
                        {user.email}
                      </p>
                      <p className="mt-0.5 font-sans text-xs text-ink-muted">
                        {user.role === "owner" ? "Pemilik toko" : "Pembeli"}
                      </p>
                    </div>
                    <Link
                      to="/dashboard"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 font-sans text-sm
                                 text-ink-muted hover:text-accent hover:bg-accent/5"
                    >
                      <i className="fa-solid fa-box-open w-4 text-center" aria-hidden />
                      Akun Saya
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); void signOut(); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 font-sans text-sm
                                 text-ink-muted hover:text-hot hover:bg-hot/5"
                    >
                      <i className="fa-solid fa-arrow-right-from-bracket w-4 text-center" aria-hidden />
                      Keluar
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Button variant="primary" onClick={handleSignIn}>
              Masuk
            </Button>
          )}

          {/* Hamburger hanya dirender kalau ada link yang bisa dibuka — daftar
              `visible` ikut menyusut mengikuti role, jadi jangan tampilkan
              tombol yang membuka panel kosong. */}
          {visible.length > 0 && (
            <button
              type="button"
              onClick={() => setNavOpen((v) => !v)}
              aria-label="Menu navigasi"
              aria-expanded={navOpen}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-sm
                         border border-border text-ink-muted
                         hover:text-accent hover:border-accent transition-colors"
            >
              <i
                className={`fa-solid ${navOpen ? "fa-xmark" : "fa-bars"} text-sm`}
                aria-hidden
              />
            </button>
          )}
        </div>
      </div>

      {navOpen && (
        <nav className="md:hidden border-t border-border px-4 py-1.5 flex flex-col">
          {visible.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              // Tanpa ini panel tetap terbuka menutupi halaman tujuan.
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                `font-sans text-sm font-medium px-2 py-2.5 rounded-sm ${
                  isActive ? "text-accent" : "text-ink-muted hover:text-ink-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}

      {/* Kegagalan login harus terlihat — sebelumnya error ditelan diam-diam
          sehingga user mengira "sudah login tapi tidak terjadi apa-apa". */}
      {error && !user && (
        <div
          role="alert"
          className="border-t border-status-failed/40 bg-status-failed/10 px-4 py-2"
        >
          <div className="max-w-7xl mx-auto">
            <MonoText size="xs" color="status-failed">[ AUTH_ERROR ]</MonoText>
            <p className="mt-1 font-mono text-2xs text-ink-primary leading-relaxed">
              {error}
            </p>
          </div>
        </div>
      )}
    </header>
  );
}